import { ChevronUp, Clock3 } from "lucide-react";
import { createContext, useContext, useEffect, useRef, useState } from "react";

const DEFAULT_MINUTES = 30;
const MIN_MINUTES = 1;
const MAX_MINUTES = 180;
const TICK_INTERVAL_MS = 250;
const STORAGE_KEY = "learnflow.study-timer.minutes";
const PRESET_MINUTES = [60, 30, 15] as const;

type StudyTimerState = {
  selectedMinutes: number;
  remainingSeconds: number;
  drawerOpen: boolean;
  running: boolean;
  alarming: boolean;
  setDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectMinutes: (minutes: number) => void;
  start: () => void;
  stop: () => void;
  reset: () => void;
};

const StudyTimerContext = createContext<StudyTimerState | null>(null);

export function StudyTimerProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [selectedMinutes, setSelectedMinutes] = useState(readStoredMinutes);
  const [remainingSeconds, setRemainingSeconds] = useState(() => selectedMinutes * 60);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [alarming, setAlarming] = useState(false);
  const deadline = useRef<number | null>(null);
  const tickHandle = useRef<number | null>(null);

  const clearTick = (): void => {
    if (tickHandle.current !== null) {
      window.clearInterval(tickHandle.current);
      tickHandle.current = null;
    }
    deadline.current = null;
  };

  const clearAlarm = (): void => {
    setAlarming(false);
  };

  const finish = (): void => {
    clearTick();
    setRunning(false);
    setRemainingSeconds(0);
    setAlarming(true);
    setDrawerOpen(true);
  };

  const updateFromDeadline = (): void => {
    if (deadline.current === null) return;
    const nextRemaining = Math.max(0, Math.ceil((deadline.current - Date.now()) / 1_000));
    if (nextRemaining === 0) {
      finish();
      return;
    }
    setRemainingSeconds(nextRemaining);
  };

  useEffect(
    () => () => {
      if (tickHandle.current !== null) window.clearInterval(tickHandle.current);
    },
    [],
  );

  const selectMinutes = (minutes: number): void => {
    const nextMinutes = Math.min(MAX_MINUTES, Math.max(MIN_MINUTES, minutes));
    clearTick();
    clearAlarm();
    setRunning(false);
    setSelectedMinutes(nextMinutes);
    setRemainingSeconds(nextMinutes * 60);
    storeMinutes(nextMinutes);
  };

  const start = (): void => {
    if (tickHandle.current !== null || alarming || remainingSeconds <= 0) return;
    deadline.current = Date.now() + remainingSeconds * 1_000;
    setRunning(true);
    tickHandle.current = window.setInterval(updateFromDeadline, TICK_INTERVAL_MS);
  };

  const stop = (): void => {
    if (alarming) {
      clearAlarm();
      return;
    }
    if (deadline.current === null) return;
    const nextRemaining = Math.max(0, Math.ceil((deadline.current - Date.now()) / 1_000));
    clearTick();
    setRunning(false);
    if (nextRemaining === 0) {
      setRemainingSeconds(0);
      setAlarming(true);
      setDrawerOpen(true);
      return;
    }
    setRemainingSeconds(nextRemaining);
  };

  const reset = (): void => {
    clearTick();
    clearAlarm();
    setRunning(false);
    setRemainingSeconds(selectedMinutes * 60);
  };

  return (
    <StudyTimerContext.Provider
      value={{
        selectedMinutes,
        remainingSeconds,
        drawerOpen,
        running,
        alarming,
        setDrawerOpen,
        selectMinutes,
        start,
        stop,
        reset,
      }}
    >
      {children}
    </StudyTimerContext.Provider>
  );
}

export function StudyTimer(): React.JSX.Element {
  const {
    selectedMinutes,
    remainingSeconds,
    drawerOpen,
    running,
    alarming,
    setDrawerOpen,
    selectMinutes,
    start,
    stop,
    reset,
  } = useStudyTimer();
  const displayTime = formatTime(remainingSeconds);
  const timerLabel = alarming
    ? "Timer expired"
    : `${Math.floor(remainingSeconds / 60)} minutes and ${remainingSeconds % 60} seconds remaining`;

  return (
    <>
      <section
        className={[
          "study-timer",
          drawerOpen ? "study-timer-open" : "",
          alarming ? "study-timer-finished" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        aria-label="Study timer"
      >
        <button
          id="study-timer-trigger"
          className="study-timer-trigger"
          type="button"
          aria-expanded={drawerOpen}
          aria-controls="study-timer-panel"
          onClick={() => setDrawerOpen((open) => !open)}
        >
          <Clock3 className="study-timer-symbol" size={17} aria-hidden="true" />
          <span className="study-timer-trigger-label">
            <strong>Create a timer</strong>
            <small>Time-box this session</small>
          </span>
          <ChevronUp className="study-timer-chevron" size={14} aria-hidden="true" />
        </button>

        <div
          id="study-timer-panel"
          className="study-timer-panel"
          aria-hidden={!drawerOpen}
          aria-labelledby="study-timer-trigger"
        >
          <div className="study-timer-panel-inner">
            <div className="study-timer-controls">
              <div className="study-timer-lcd-wrap">
                <div
                  className="study-timer-lcd"
                  role="timer"
                  aria-live="off"
                  aria-label={timerLabel}
                >
                  <span className="study-timer-time">{displayTime}</span>
                </div>
                <div className="study-timer-stepper" aria-label="Fine-tune timer">
                  <button
                    type="button"
                    aria-label="Add one minute"
                    disabled={running || alarming || selectedMinutes >= MAX_MINUTES}
                    onClick={() => selectMinutes(selectedMinutes + 1)}
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    aria-label="Remove one minute"
                    disabled={running || alarming || selectedMinutes <= MIN_MINUTES}
                    onClick={() => selectMinutes(selectedMinutes - 1)}
                  >
                    ▼
                  </button>
                </div>
              </div>

              <span className="study-timer-preset-label">Minutes</span>
              <div className="study-timer-presets" aria-label="Timer presets">
                {PRESET_MINUTES.map((minutes) => (
                  <button
                    key={minutes}
                    className="study-timer-preset"
                    type="button"
                    aria-pressed={selectedMinutes === minutes}
                    disabled={running || alarming}
                    onClick={() => selectMinutes(minutes)}
                  >
                    {minutes}
                  </button>
                ))}
              </div>

              <div className="study-timer-actions">
                <button
                  className="study-timer-start"
                  type="button"
                  disabled={running || alarming || remainingSeconds <= 0}
                  onClick={start}
                >
                  Start
                </button>
                <button type="button" disabled={!running && !alarming} onClick={stop}>
                  Stop
                </button>
                <button type="button" onClick={reset}>
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      <span className="sr-only" aria-live="assertive">
        {alarming ? "Time is up." : ""}
      </span>
    </>
  );
}

function useStudyTimer(): StudyTimerState {
  const timer = useContext(StudyTimerContext);
  if (!timer) throw new Error("StudyTimer must be rendered inside StudyTimerProvider.");
  return timer;
}

function readStoredMinutes(): number {
  try {
    const storedValue = window.localStorage.getItem(STORAGE_KEY);
    if (storedValue === null) return DEFAULT_MINUTES;
    const minutes = Number(storedValue);
    return isValidMinutes(minutes) ? minutes : DEFAULT_MINUTES;
  } catch {
    return DEFAULT_MINUTES;
  }
}

function storeMinutes(minutes: number): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(minutes));
  } catch {
    // The in-memory timer remains usable when browser storage is unavailable.
  }
}

function isValidMinutes(minutes: number): boolean {
  return Number.isInteger(minutes) && minutes >= MIN_MINUTES && minutes <= MAX_MINUTES;
}

function formatTime(seconds: number): string {
  const safeSeconds = Math.max(0, Math.ceil(seconds));
  const minutes = Math.floor(safeSeconds / 60);
  const remainder = safeSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(remainder).padStart(2, "0")}`;
}
